import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { windDirections } from '../RoofAssignment/constants'

interface Bounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

const getBounds = (elements: ShapeObject[]): Bounds | null => {
  const points = elements.flatMap(element => element.shape.points)
  if (points.length === 0) return null

  return points.reduce<Bounds>(
    (acc, point) => ({
      minX: Math.min(acc.minX, point.x),
      maxX: Math.max(acc.maxX, point.x),
      minY: Math.min(acc.minY, point.y),
      maxY: Math.max(acc.maxY, point.y),
      minZ: Math.min(acc.minZ, point.z),
      maxZ: Math.max(acc.maxZ, point.z),
    }),
    {
      minX: points[0].x,
      maxX: points[0].x,
      minY: points[0].y,
      maxY: points[0].y,
      minZ: points[0].z,
      maxZ: points[0].z,
    },
  )
}

const calculateSectionValue = (bOrthogonal: number, height: number) =>
  Math.min(bOrthogonal, 2 * height)

export const vector = (x: number, y: number, z = 0) => new ImmutableVector3(x, y, z)

export const DEFAULT_BUILDING_DIMENSIONS: BuildingDimensionsDataWithStart = {
  width: 10,
  depth: 10,
  height: 10,
  start: vector(0, 0, 0),
}

type RoofAssignmentByDirection = Partial<Record<WindDirection, { configurations?: RoofConfig[] }>>

export const getAssignedWallIds = (wallAssignment?: WindLoadWallAssignment): string[] =>
  Array.from(
    new Set([
      ...(wallAssignment?.elements_wind_D ?? []),
      ...(wallAssignment?.elements_wind_E ?? []),
      ...(wallAssignment?.elements_wind_ABC_left_main ?? []),
      ...(wallAssignment?.elements_wind_ABC_right_main ?? []),
    ]),
  )

export const hasAssignedWalls = (wallAssignment?: WindLoadWallAssignment): boolean =>
  getAssignedWallIds(wallAssignment).length > 0

export const hasCompleteRoofAssignment = (roofAssignment?: RoofAssignmentByDirection): boolean =>
  windDirections.every(direction => (roofAssignment?.[direction]?.configurations?.length ?? 0) > 0)

export const isMissingOrInvalidDimensions = (dimensions?: BuildingDimensionsDataWithStart) => {
  if (!dimensions) return true
  return dimensions.height <= 0 || dimensions.depth <= 0 || dimensions.width <= 0
}

export const normalizeDimensionsStart = (
  dimensions: BuildingDimensionsDataWithStart,
): BuildingDimensionsDataWithStart => ({
  ...dimensions,
  start: dimensions.start ?? vector(0, 0, 0),
})

export const calculateWindBoxConfig = (
  buildingDimensions: BuildingDimensionsDataWithStart,
): WindBoxConfig => {
  const { start, width, depth, height } = buildingDimensions
  const normalizedStart = vector(start.x, start.y, start.z ?? 0)

  const createConfigForDirection = (direction: WindDirection): WindBoxConfig[WindDirection] => {
    const dParallel = direction === 'Direction0' || direction === 'Direction180' ? depth : width
    const bOrthogonal = direction === 'Direction0' || direction === 'Direction180' ? width : depth
    const sectionValue = calculateSectionValue(bOrthogonal, height)

    if (direction === 'Direction0') {
      return {
        D: {
          start: vector(normalizedStart.x, normalizedStart.y + bOrthogonal, normalizedStart.z),
          direction: vector(0, -1, 0),
          length: bOrthogonal,
          section_value: sectionValue,
        },
        E: {
          start: vector(normalizedStart.x + dParallel, normalizedStart.y, normalizedStart.z),
          direction: vector(0, 1, 0),
          length: bOrthogonal,
          section_value: sectionValue,
        },
        ABC_right: {
          start: normalizedStart,
          direction: vector(1, 0, 0),
          length: dParallel,
          section_value: sectionValue,
        },
        ABC_left: {
          start: vector(normalizedStart.x, normalizedStart.y + bOrthogonal, normalizedStart.z),
          direction: vector(1, 0, 0),
          length: dParallel,
          section_value: sectionValue,
        },
      }
    }

    if (direction === 'Direction90') {
      return {
        D: {
          start: normalizedStart,
          direction: vector(1, 0, 0),
          length: bOrthogonal,
          section_value: sectionValue,
        },
        E: {
          start: vector(
            normalizedStart.x + bOrthogonal,
            normalizedStart.y + dParallel,
            normalizedStart.z,
          ),
          direction: vector(-1, 0, 0),
          length: bOrthogonal,
          section_value: sectionValue,
        },
        ABC_right: {
          start: normalizedStart,
          direction: vector(0, 1, 0),
          length: dParallel,
          section_value: sectionValue,
        },
        ABC_left: {
          start: vector(normalizedStart.x + bOrthogonal, normalizedStart.y, normalizedStart.z),
          direction: vector(0, 1, 0),
          length: dParallel,
          section_value: sectionValue,
        },
      }
    }

    if (direction === 'Direction180') {
      return {
        D: {
          start: vector(normalizedStart.x + dParallel, normalizedStart.y, normalizedStart.z),
          direction: vector(0, 1, 0),
          length: bOrthogonal,
          section_value: sectionValue,
        },
        E: {
          start: vector(normalizedStart.x, normalizedStart.y + bOrthogonal, normalizedStart.z),
          direction: vector(0, -1, 0),
          length: bOrthogonal,
          section_value: sectionValue,
        },
        ABC_left: {
          start: vector(normalizedStart.x + dParallel, normalizedStart.y, normalizedStart.z),
          direction: vector(-1, 0, 0),
          length: dParallel,
          section_value: sectionValue,
        },
        ABC_right: {
          start: vector(
            normalizedStart.x + dParallel,
            normalizedStart.y + bOrthogonal,
            normalizedStart.z,
          ),
          direction: vector(-1, 0, 0),
          length: dParallel,
          section_value: sectionValue,
        },
      }
    }

    return {
      D: {
        start: vector(
          normalizedStart.x + bOrthogonal,
          normalizedStart.y + dParallel,
          normalizedStart.z,
        ),
        direction: vector(-1, 0, 0),
        length: bOrthogonal,
        section_value: sectionValue,
      },
      E: {
        start: normalizedStart,
        direction: vector(1, 0, 0),
        length: bOrthogonal,
        section_value: sectionValue,
      },
      ABC_right: {
        start: vector(
          normalizedStart.x + bOrthogonal,
          normalizedStart.y + dParallel,
          normalizedStart.z,
        ),
        direction: vector(0, -1, 0),
        length: dParallel,
        section_value: sectionValue,
      },
      ABC_left: {
        start: vector(normalizedStart.x, normalizedStart.y + dParallel, normalizedStart.z),
        direction: vector(0, -1, 0),
        length: dParallel,
        section_value: sectionValue,
      },
    }
  }

  return {
    Direction0: createConfigForDirection('Direction0'),
    Direction90: createConfigForDirection('Direction90'),
    Direction180: createConfigForDirection('Direction180'),
    Direction270: createConfigForDirection('Direction270'),
  }
}

export const calculateBuildingDimensions = (
  model: PlanarModel,
  wallAssignment?: WindLoadWallAssignment,
) => {
  const roofBounds = getBounds(model.roof_slabs)
  const wallByGuid = new Map(model.walls.map(wall => [wall.guid, wall]))
  const assignedWalls = getAssignedWallIds(wallAssignment)
    .map(wallId => wallByGuid.get(wallId))
    .filter((wall): wall is Wall => wall !== undefined)

  if (assignedWalls.length === 0) {
    return {
      roofDimensions: DEFAULT_BUILDING_DIMENSIONS,
      qZoneDimensions: DEFAULT_BUILDING_DIMENSIONS,
      wallDimensions: DEFAULT_BUILDING_DIMENSIONS,
    }
  }

  const wallBounds = getBounds(assignedWalls)
  const qZoneBounds = getBounds([
    ...model.walls.filter(wall => wall.placement === 'External'),
    ...model.slabs,
    ...model.roof_slabs,
  ])

  const roofDimensions = roofBounds
    ? {
        height: roofBounds.maxZ,
        depth: roofBounds.maxX - roofBounds.minX,
        width: roofBounds.maxY - roofBounds.minY,
        start: vector(roofBounds.minX, roofBounds.minY, 0),
      }
    : undefined

  const qZoneDimensions =
    qZoneBounds && roofBounds
      ? {
          height: roofBounds.maxZ,
          depth: qZoneBounds.maxX - qZoneBounds.minX,
          width: qZoneBounds.maxY - qZoneBounds.minY,
          start: vector(qZoneBounds.minX, qZoneBounds.minY, 0),
        }
      : undefined

  const wallDimensions = (() => {
    if (wallBounds && roofBounds) {
      return {
        height: roofBounds.maxZ,
        depth: wallBounds.maxX - wallBounds.minX,
        width: wallBounds.maxY - wallBounds.minY,
        start: vector(wallBounds.minX, wallBounds.minY, 0),
      }
    }

    if (roofBounds) {
      return {
        height: roofBounds.maxZ,
        depth: roofBounds.maxX - roofBounds.minX,
        width: roofBounds.maxY - roofBounds.minY,
        start: vector(roofBounds.minX, roofBounds.minY, 0),
      }
    }

    return undefined
  })()

  return { roofDimensions, qZoneDimensions, wallDimensions }
}
